import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  forwardRef,
  input,
  signal,
  computed,
  effect,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { InputComponent } from 'src/app/ui-kit/form-controls/input/input.component';
import { InputLabelComponent } from 'src/app/ui-kit/form-controls/input-label/input-label.component';
import { CheckboxInputComponent } from 'src/app/ui-kit/form-controls/checkbox-input/checkbox-input.component';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import Fuse from 'fuse.js';

/**
 * Typeahead Input Component
 * 
 * A searchable dropdown input that supports fuzzy filtering and both single/multi-select modes.
 * Implements ControlValueAccessor for seamless integration with Angular reactive forms.
 * 
 * @example
 * // Single-select mode
 * <app-typeahead-input
 *   label="Select User"
 *   placeholder="Search users..."
 *   [options]="users"
 *   [displayFn]="getUserName"
 *   formControlName="selectedUser"
 * ></app-typeahead-input>
 * 
 * @example
 * // Multi-select mode
 * <app-typeahead-input
 *   label="Add Students"
 *   placeholder="Search students..."
 *   [multiSelect]="true"
 *   [options]="students"
 *   [displayFn]="getStudentName"
 *   formControlName="caseload"
 * ></app-typeahead-input>
 */
@Component({
  selector: 'app-typeahead-input',
  templateUrl: './typeahead-input.component.html',
  styleUrls: ['./typeahead-input.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    InputLabelComponent,
    CheckboxInputComponent,
    ChipComponent,
    IconComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadInputComponent),
      multi: true,
    },
  ],
  host: {
    class: 'block',
  },
})
export class TypeaheadInputComponent<T = any>
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('searchInput', { read: ElementRef })
  searchInputElement?: ElementRef<HTMLInputElement>;

  /**
   * Array of options to search/select from
   */
  options = input.required<T[]>();

  /**
   * Function to convert an option to its display string
   */
  displayFn = input.required<(option: T) => string>();

  /**
   * Label for the input
   */
  label = input<string>('');

  /**
   * Placeholder text
   */
  placeholder = input<string>('Search...');

  /**
   * Whether this is a required field
   */
  required = input<boolean>(false);

  /**
   * Whether to allow multiple selections
   */
  multiSelect = input<boolean>(false);

  /**
   * Search control
   */
  searchControl = new FormControl('');

  /**
   * Current selected values (always an array)
   */
  private selectedValues = signal<T[]>([]);

  /**
   * Search term signal (synced with searchControl)
   */
  private searchTerm = signal('');

  /**
   * Filtered options based on search
   */
  filteredOptions = computed(() => {
    const term = this.searchTerm();
    const opts = this.options();
    const displayFunc = this.displayFn();

    if (!term || term.trim() === '') {
      return opts.slice(0, 10); // Limit to 10 results even when no search
    }

    // Use Fuse.js for fuzzy searching
    const fuse = new Fuse(opts, {
      keys: ['searchField'],
      threshold: 0.3,
      getFn: (obj: T | object, _path: string | string[]) => {
        // Always return the display string for searching
        return displayFunc(obj as T);
      },
    });

    // Limit to 10 results
    return fuse.search(term).map(result => result.item).slice(0, 10);
  });

  /**
   * Whether to show the dropdown - show whenever search input has text
   */
  showDropdown = computed(() => {
    const term = this.searchTerm();
    return !!term && term.length > 0;
  });

  /**
   * Disabled state
   */
  disabled = signal(false);

  private destroy$ = new Subject<void>();
  private onChange: (value: T[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Watch for search control changes and sync to searchTerm signal
    this.searchControl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        this.searchTerm.set(value || '');
      });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * ControlValueAccessor: Write value
   */
  writeValue(value: T[]): void {
    if (value && Array.isArray(value)) {
      this.selectedValues.set(value);
    } else {
      this.selectedValues.set([]);
    }
  }

  /**
   * ControlValueAccessor: Register onChange callback
   */
  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor: Register onTouched callback
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor: Set disabled state
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  /**
   * Check if an option is selected
   */
  isSelected(option: T): boolean {
    return this.selectedValues().includes(option);
  }

  /**
   * Handle option selection
   */
  onOptionClick(option: T): void {
    const currentValues = this.selectedValues();
    let newValues: T[];

    if (currentValues.includes(option)) {
      // Remove from selection
      newValues = currentValues.filter((v) => v !== option);
    } else {
      // Add to selection
      if (this.multiSelect()) {
        newValues = [...currentValues, option];
      } else {
        newValues = [option];
      }
    }

    this.selectedValues.set(newValues);
    this.onChange(newValues);

    // Clear search input (this will hide the dropdown)
    this.searchControl.setValue('', { emitEvent: true });
    
    // Keep focus on input - no setTimeout needed with mousedown
    this.searchInputElement?.nativeElement.focus();
    
    // Call onTouched after refocusing
    this.onTouched();
  }

  /**
   * Handle input focus
   */
  onFocus(): void {
    // No special logic needed - dropdown visibility is controlled by searchControl.value
  }

  /**
   * Handle input blur
   */
  onBlur(): void {
    this.onTouched();
    // Keep search input as-is, don't clear or hide dropdown
  }

  /**
   * Get display string for an option
   */
  getDisplayString(option: T): string {
    return this.displayFn()(option);
  }

  /**
   * Get count of selected items
   */
  getSelectedCount(): number {
    return this.selectedValues().length;
  }

  /**
   * Get selected values for display
   */
  getSelectedValues(): T[] {
    return this.selectedValues();
  }

  /**
   * Handle chip removal
   */
  onRemoveChip(option: T): void {
    const currentValues = this.selectedValues();
    const newValues = currentValues.filter((v) => v !== option);
    this.selectedValues.set(newValues);
    this.onChange(newValues);
    this.onTouched();
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Removed - we don't clear search on outside click anymore
    // Search persists until user manually clears it
  }
}

